"use client";

import { c as _c } from "react/compiler-runtime";
import { CalendarClock, MapPin, Stethoscope, User } from "lucide-react";
import { useState } from "react";
import type { AvailabilityAppointmentSlot } from "@/shared";
import { StepActions } from "@/src/components/online-scheduling/step-actions";
import { SummaryPanel, SummaryRow } from "@/src/components/online-scheduling/summary";
import { formatLocality, isMinorDateOfBirth, type OnlineSchedulingLocation, type PatientDetailsDraft } from "@/src/components/online-scheduling/types";
import { Alert, AlertDescription, AlertTitle } from "@/src/components/shadcn/ui/alert";
import { Checkbox } from "@/src/components/shadcn/ui/checkbox";
import { Label } from "@/src/components/shadcn/ui/label";
import { formatSlotDateTime } from "@/src/lib/scheduling/slot-display";
export function ReviewStep(t0) {
  const $ = _c(94);
  const {
    location,
    isReturningPatient,
    reasonForVisit,
    slot,
    details,
    onBack,
    onConfirm,
    isBooking,
    bookingError
  } = t0;
  let t1;
  if ($[0] !== location) {
    t1 = formatLocality(location);
    $[0] = location;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const locality = t1;
  const [hasConsented, setHasConsented] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CalendarClock className="h-4 w-4" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== slot.start) {
    t3 = formatSlotDateTime(slot.start);
    $[3] = slot.start;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <p className="font-medium">{t3}</p>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== slot.practitionerDisplayName) {
    t5 = slot.practitionerDisplayName && <p className="text-muted-foreground">with {slot.practitionerDisplayName}</p>;
    $[7] = slot.practitionerDisplayName;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <SummaryRow icon={t2} label="Appointment">{t4}{t5}</SummaryRow>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MapPin className="h-4 w-4" />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== location.displayName) {
    t8 = <p className="font-medium">{location.displayName}</p>;
    $[13] = location.displayName;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== location.addressLine1) {
    t9 = location.addressLine1 && <p className="text-muted-foreground">{location.addressLine1}</p>;
    $[15] = location.addressLine1;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== locality) {
    t10 = locality && <p className="text-muted-foreground">{locality}</p>;
    $[17] = locality;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8 || $[21] !== t9) {
    t11 = <SummaryRow icon={t7} label="Location">{t8}{t9}{t10}</SummaryRow>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Stethoscope className="h-4 w-4" />;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = isReturningPatient ? "Returning patient" : "New patient";
  let t14;
  if ($[24] !== t13) {
    t14 = <p className="font-medium">{t13}</p>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== reasonForVisit) {
    t15 = reasonForVisit.trim() && <p className="text-muted-foreground">{reasonForVisit.trim()}</p>;
    $[26] = reasonForVisit;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = <SummaryRow icon={t12} label="Visit">{t14}{t15}</SummaryRow>;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <User className="h-4 w-4" />;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== details.firstName || $[33] !== details.lastName) {
    t18 = <p className="font-medium">{details.firstName} {details.lastName}</p>;
    $[32] = details.firstName;
    $[33] = details.lastName;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== details.dateOfBirth) {
    t19 = <p className="text-muted-foreground">DOB {details.dateOfBirth}</p>;
    $[35] = details.dateOfBirth;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== details.gender) {
    t20 = details.gender && <p className="text-muted-foreground">{details.gender}</p>;
    $[37] = details.gender;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== details.phone) {
    t21 = <p className="text-muted-foreground">{details.phone}</p>;
    $[39] = details.phone;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== details.homeAddress.line1 || $[42] !== details.homeAddress.line2) {
    t22 = [details.homeAddress.line1, details.homeAddress.line2].filter(Boolean);
    $[41] = details.homeAddress.line1;
    $[42] = details.homeAddress.line2;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = t22.join(", ");
  let t24;
  if ($[44] !== t23) {
    t24 = <p className="break-words text-muted-foreground">{t23}</p>;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== details.homeAddress.city || $[47] !== details.homeAddress.postalCode || $[48] !== details.homeAddress.state) {
    t25 = <p className="text-muted-foreground">{details.homeAddress.city}, {details.homeAddress.state} {details.homeAddress.postalCode}</p>;
    $[46] = details.homeAddress.city;
    $[47] = details.homeAddress.postalCode;
    $[48] = details.homeAddress.state;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== details.insuranceMemberId || $[51] !== details.insurancePayer) {
    t26 = (details.insurancePayer.trim() || details.insuranceMemberId.trim()) && <p className="text-muted-foreground">{details.insurancePayer.trim() || "Insurance"}{details.insuranceMemberId.trim() ? ` · #${details.insuranceMemberId.trim()}` : ""}</p>;
    $[50] = details.insuranceMemberId;
    $[51] = details.insurancePayer;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== details.dateOfBirth || $[54] !== details.guardianFirstName || $[55] !== details.guardianLastName || $[56] !== details.guardianPhone) {
    t27 = isMinorDateOfBirth(details.dateOfBirth) && (details.guardianFirstName.trim() || details.guardianLastName.trim() || details.guardianPhone.trim()) && <p className="text-muted-foreground">Guardian:{" "}{[details.guardianFirstName.trim(), details.guardianLastName.trim()].filter(Boolean).join(" ")}{details.guardianPhone.trim() ? ` · ${details.guardianPhone.trim()}` : ""}</p>;
    $[53] = details.dateOfBirth;
    $[54] = details.guardianFirstName;
    $[55] = details.guardianLastName;
    $[56] = details.guardianPhone;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t18 || $[59] !== t19 || $[60] !== t20 || $[61] !== t21 || $[62] !== t24 || $[63] !== t25 || $[64] !== t26 || $[65] !== t27) {
    t28 = <SummaryRow icon={t17} label="Patient">{t18}{t19}{t20}{t21}{t24}{t25}{t26}{t27}</SummaryRow>;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t24;
    $[63] = t25;
    $[64] = t26;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t11 || $[68] !== t16 || $[69] !== t28 || $[70] !== t6) {
    t29 = <SummaryPanel>{t6}{t11}{t16}{t28}</SummaryPanel>;
    $[67] = t11;
    $[68] = t16;
    $[69] = t28;
    $[70] = t6;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== bookingError) {
    t30 = bookingError && <Alert variant="destructive"><AlertTitle>We couldn't book that time</AlertTitle><AlertDescription>{bookingError}</AlertDescription></Alert>;
    $[72] = bookingError;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = checked => setHasConsented(checked === true);
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== hasConsented || $[76] !== isBooking) {
    t32 = <Checkbox id="contact-consent" checked={hasConsented} onCheckedChange={t31} disabled={isBooking} />;
    $[75] = hasConsented;
    $[76] = isBooking;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Label htmlFor="contact-consent" className="text-xs font-normal leading-snug text-muted-foreground">By confirming, you agree to be contacted about this appointment over phone or email.</Label>;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== t32) {
    t34 = <div className="flex items-center gap-2">{t32}{t33}</div>;
    $[79] = t32;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== hasConsented || $[82] !== onConfirm) {
    t35 = () => onConfirm(hasConsented);
    $[81] = hasConsented;
    $[82] = onConfirm;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  const t36 = !hasConsented;
  let t37;
  if ($[84] !== isBooking || $[85] !== onBack || $[86] !== t35 || $[87] !== t36) {
    t37 = <StepActions primaryLabel="Confirm appointment" onPrimary={t35} primaryDisabled={t36} isBusy={isBooking} onBack={onBack} backDisabled={isBooking} />;
    $[84] = isBooking;
    $[85] = onBack;
    $[86] = t35;
    $[87] = t36;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== t29 || $[90] !== t30 || $[91] !== t34 || $[92] !== t37) {
    t38 = <div className="space-y-6">{t29}{t30}{t34}{t37}</div>;
    $[89] = t29;
    $[90] = t30;
    $[91] = t34;
    $[92] = t37;
    $[93] = t38;
  } else {
    t38 = $[93];
  }
  return t38;
}